import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { FileText, PenTool, Plus, Save, Trash2, Upload } from "lucide-react";
import { COLOR_FIELDS, offerLabel } from "@/lib/constants";
import { fmtCurrency } from "@/lib/quote-helpers";
import { ReportSettingsCard } from "@/components/ReportSettingsCard";
import { DemoShareButton } from "@/components/DemoShareButton";
import { TeamCard } from "@/components/TeamCard";
import { AuditLogCard } from "@/components/AuditLogCard";
import { ApiKeysCard } from "@/components/ApiKeysCard";
import { ModulesCard } from "@/components/ModulesCard";
import { IntegrationsCard } from "@/components/IntegrationsCard";
import { IntegrationCenter } from "@/components/IntegrationCenter";
import { AISettingsCard } from "@/components/AISettingsCard";

export const SettingsTab = ({
  subscription, company, hasFeature,
  companyForm, setC, setCompanyColor, saveCompany, onCompanyLogo,
  connect, connectLoading, startConnectOnboard, openConnectDashboard,
  payCfg, payForm, setPayForm, savePayment,
  emailCfg, emailForm, setEmailForm, saveEmail,
  remindersForm, setRemindersForm, remindersSaving, saveReminders, runPredueNow, runFollowupNow,
  datev, setDatev, saveDatev,
  steuer, setSteuer, saveSteuer,
  packages, newPkg, setNewPkg, createPackage, deletePackage,
  attachments, fileInputRefs, uploadAttachment, removeAttachment, renameAttachment,
  signature, removeSignature, uploadSignatureFile, setSigDraft, setSigPadOpen, setAutoSigOpen,
  openBillingPortal, cancelSubscription, resumeSubscription,
}) => (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
              {/* Mein Abo */}
              <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-subscription-card">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">Mein Abo</h2>
                {subscription && subscription.demo ? (
                  <p className="text-sm text-[var(--brand-muted)]">Demo-Zugang – voller Funktionsumfang (Business). Kein echtes Abo.</p>
                ) : subscription && subscription.active ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm text-[var(--brand-ink)]">
                      <span className="inline-block px-2.5 py-1 rounded-full bg-[var(--brand-primary)] text-white text-xs font-semibold mr-2 capitalize" data-testid="sub-plan-badge">{subscription.license === "whitelabel" ? "White-Label-Lizenz" : subscription.bundle === "werkstatt" ? "Werkstatt-Bundle" : subscription.plan}</span>
                      {subscription.license === "whitelabel" ? "Dauerhaft" : subscription.bundle === "werkstatt" ? "Monatlich" : subscription.interval === "year" ? "Jährlich" : subscription.interval === "ablefy" ? "Einmalkauf" : "Monatlich"}
                      {subscription.cancel_at_period_end ? (
                        <span className="text-amber-600 font-medium"> · gekündigt – aktiv bis {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString("de-DE") : "Periodenende"}, keine weitere Verlängerung</span>
                      ) : (
                        subscription.current_period_end && (<span className="text-[var(--brand-muted)]"> · verlängert sich am {new Date(subscription.current_period_end).toLocaleDateString("de-DE")}</span>)
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {subscription.license !== "whitelabel" && (
                        <Button variant="outline" size="sm" onClick={openBillingPortal} data-testid="manage-subscription-btn">Zahlungsdaten / Rechnungen</Button>
                      )}
                      {subscription.license !== "whitelabel" && (subscription.cancel_at_period_end ? (
                        <Button size="sm" onClick={resumeSubscription} data-testid="resume-subscription-btn">Kündigung zurücknehmen</Button>
                      ) : (
                        <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" onClick={cancelSubscription} data-testid="cancel-subscription-btn">Abo kündigen</Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[var(--brand-muted)]">Kein aktives Abo.</p>
                )}
              </Card>
              {/* Module & Add-ons */}
              <ModulesCard subscription={subscription} />
              {/* Eigene API-Schlüssel (mandantenfähig) */}
              <AISettingsCard />
              <IntegrationsCard />
              <IntegrationCenter />
              {/* White-Label-Auszahlungen (Stripe Connect) – nur für White-Label-Lizenznehmer */}
              {subscription?.license === "whitelabel" && (
              <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-connect-card">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">White-Label-Auszahlungen (Stripe Connect)</h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Verbinde dein Stripe-Konto, um Zahlungen deiner Endkunden zu empfangen. Aus jeder Zahlung gehen automatisch <strong>30 %</strong> Lizenzbeteiligung an den Plattform-Betreiber und 70 % an dich.
                </p>
                {connect?.charges_enabled ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold" data-testid="connect-status-active">✓ Aktiv – Auszahlungen aktiviert</span>
                    <Button variant="outline" size="sm" onClick={openConnectDashboard} data-testid="connect-dashboard-btn">Stripe-Dashboard öffnen</Button>
                  </div>
                ) : connect?.account_id ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold" data-testid="connect-status-pending">Onboarding noch nicht abgeschlossen</span>
                    <Button onClick={startConnectOnboard} disabled={connectLoading} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="connect-continue-btn">{connectLoading ? "Weiterleitung …" : "Onboarding fortsetzen"}</Button>
                  </div>
                ) : (
                  <Button onClick={startConnectOnboard} disabled={connectLoading} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="connect-onboard-btn">{connectLoading ? "Weiterleitung …" : "Stripe Connect einrichten"}</Button>
                )}
              </Card>
              )}

              {/* Firmendaten & Branding */}
              <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-company-card">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">
                  Firmendaten & Branding
                </h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Diese Angaben erscheinen als Absender auf jedem Angebot. Farben & Logo bestimmen Ihr Erscheinungsbild.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <Label>Firmenname</Label>
                      <Input value={companyForm.company_name || ""} onChange={(e) => setC("company_name", e.target.value)} data-testid="settings-company-name" />
                    </div>
                    <div>
                      <Label>Inhaber / Ansprechpartner</Label>
                      <Input value={companyForm.owner_name || ""} onChange={(e) => setC("owner_name", e.target.value)} data-testid="settings-owner" />
                    </div>
                    <div>
                      <Label>Straße & Hausnr.</Label>
                      <Input value={companyForm.strasse || ""} onChange={(e) => setC("strasse", e.target.value)} data-testid="settings-strasse" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>PLZ & Ort</Label>
                        <Input value={companyForm.plz_ort || ""} onChange={(e) => setC("plz_ort", e.target.value)} data-testid="settings-plzort" />
                      </div>
                      <div>
                        <Label>Land</Label>
                        <Input value={companyForm.land || ""} onChange={(e) => setC("land", e.target.value)} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Telefon</Label>
                        <Input value={companyForm.telefon || ""} onChange={(e) => setC("telefon", e.target.value)} data-testid="settings-telefon" />
                      </div>
                      <div>
                        <Label>E-Mail</Label>
                        <Input value={companyForm.email || ""} onChange={(e) => setC("email", e.target.value)} data-testid="settings-email" />
                      </div>
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input value={companyForm.website || ""} onChange={(e) => setC("website", e.target.value)} data-testid="settings-website" />
                    </div>
                    <div>
                      <Label>Kunden-Zugangspasswort</Label>
                      <Input value={companyForm.customer_password || ""} onChange={(e) => setC("customer_password", e.target.value)} placeholder="für Tracking-Links" data-testid="settings-customer-password" />
                    </div>
                    <div className="flex items-start justify-between gap-4 rounded-lg border border-[var(--brand-border)] p-3">
                      <div>
                        <Label className="block">Passwortschutz für Angebots-Links</Label>
                        <p className="text-xs text-[var(--brand-muted)] mt-1">
                          {companyForm.link_password_protect !== false
                            ? "Aktiv: Kunden müssen das Zugangspasswort eingeben (es wird automatisch in der E-Mail mitgesendet)."
                            : "Aus: Angebots-/Rechnungs-Links öffnen direkt ohne Passwort."}
                        </p>
                        {companyForm.link_password_protect !== false && !companyForm.customer_password && (
                          <p className="text-xs text-amber-600 mt-1">Hinweis: Bitte oben ein Zugangspasswort setzen, sonst öffnet der Link trotzdem ohne Abfrage.</p>
                        )}
                      </div>
                      <Switch
                        checked={companyForm.link_password_protect !== false}
                        onCheckedChange={(v) => setC("link_password_protect", v)}
                        data-testid="settings-link-password-protect"
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="mb-2 block">Logo</Label>
                      <div className="flex items-center gap-4">
                        <div className="h-24 w-24 rounded-xl border border-[var(--brand-border)] bg-[var(--brand-bg)] flex items-center justify-center overflow-hidden">
                          {companyForm.logo_base64 ? (
                            <img src={companyForm.logo_base64} alt="logo" className="max-h-full max-w-full object-contain" />
                          ) : (
                            <span className="text-2xl font-bold text-[var(--brand-primary)]">
                              {(companyForm.company_name || "A").trim().charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="space-y-2">
                          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--brand-primary)] text-white text-sm cursor-pointer hover:bg-[var(--brand-primary-dark)]">
                            <Upload className="w-4 h-4" /> Logo wählen
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => onCompanyLogo(e.target.files?.[0])} data-testid="settings-logo-input" />
                          </label>
                          {companyForm.logo_base64 && (
                            <button type="button" className="block text-xs text-[var(--brand-muted)] underline" onClick={() => setC("logo_base64", "")}>
                              Entfernen
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="mb-2 block">Farben</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {COLOR_FIELDS.map((c) => (
                          <div key={c.key} className="flex items-center gap-2">
                            <input
                              type="color"
                              value={companyForm[c.key] || "#000000"}
                              onChange={(e) => setCompanyColor(c.key, c.cssVar, e.target.value)}
                              className="h-9 w-10 rounded border border-[var(--brand-border)] cursor-pointer"
                              data-testid={`settings-${c.key.replace("_", "-")}`}
                            />
                            <span className="text-xs text-[var(--brand-muted)]">{c.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Disclaimer / Hinweis (optional)</Label>
                      <Textarea rows={2} value={companyForm.disclaimer || ""} onChange={(e) => setC("disclaimer", e.target.value)} data-testid="settings-disclaimer" />
                    </div>
                    
                  </div>
                </div>
                <div className="mt-6 border-t border-[var(--brand-border)] pt-5" data-testid="settings-dach-profile">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-[var(--brand-ink)]">Land, Währung & Zeitzone</h3>
                      <p className="mt-1 text-sm text-[var(--brand-muted)]">Diese Angaben steuern Cockpit, Fristen und Darstellungsformate. Die Währung und das Land eines bestehenden Angebots oder einer Rechnung bleiben immer führend.</p>
                    </div>
                    <span className="rounded-full bg-[var(--brand-bg)] px-3 py-1 text-xs font-medium text-[var(--brand-ink)]">DACH-Profil</span>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <Label className="mb-2 block">Hauptland</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={companyForm.main_country || ({ Deutschland: "DE", Österreich: "AT", Schweiz: "CH" }[companyForm.land] || "DE")}
                        onChange={(e) => {
                          const profiles = {
                            DE: { land: "Deutschland", base_currency: "EUR", locale: "de-DE", time_zone: "Europe/Berlin", languages: ["de"] },
                            AT: { land: "Österreich", base_currency: "EUR", locale: "de-AT", time_zone: "Europe/Vienna", languages: ["de"] },
                            CH: { land: "Schweiz", base_currency: "CHF", locale: "de-CH", time_zone: "Europe/Zurich", languages: ["de", "fr", "it"] },
                          };
                          const profile = profiles[e.target.value];
                          setC("main_country", e.target.value);
                          setC("land", profile.land);
                          setC("base_currency", profile.base_currency);
                          setC("locale", profile.locale);
                          setC("time_zone", profile.time_zone);
                          setC("languages", profile.languages);
                        }}
                      >
                        <option value="DE">Deutschland</option>
                        <option value="AT">Österreich</option>
                        <option value="CH">Schweiz</option>
                      </select>
                    </div>
                    <div>
                      <Label className="mb-2 block">Basiswährung</Label>
                      <Input value={companyForm.base_currency || ((companyForm.main_country === "CH" || companyForm.land === "Schweiz") ? "CHF" : "EUR")} readOnly className="bg-[var(--brand-bg)]" />
                    </div>
                    <div>
                      <Label className="mb-2 block">Zeitzone</Label>
                      <Input value={companyForm.time_zone || ((companyForm.main_country === "CH" || companyForm.land === "Schweiz") ? "Europe/Zurich" : companyForm.main_country === "AT" || companyForm.land === "Österreich" ? "Europe/Vienna" : "Europe/Berlin")} readOnly className="bg-[var(--brand-bg)]" />
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <Label className="mb-2 block">Bezeichnung für Angebote</Label>
                      <Input
                        value={companyForm.offer_label || ""}
                        onChange={(e) => setC("offer_label", e.target.value)}
                        placeholder={`Standard: ${offerLabel(companyForm)}`}
                        maxLength={50}
                        data-testid="settings-offer-label"
                      />
                      <p className="mt-1 text-xs text-[var(--brand-muted)]">Leer lassen = landesüblicher Standard. Für die Schweiz lautet dieser „Offerte“, sonst „Angebot“. Ein eigener Eintrag wird in Listen, Dokumenten und E-Mails verwendet.</p>
                    </div>
                  </div>
                  <div className="mt-4 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-bg)] p-3 text-sm text-[var(--brand-muted)]">
                    {(companyForm.main_country === "CH" || companyForm.land === "Schweiz") ? (
                      <><strong className="text-[var(--brand-ink)]">Schweiz:</strong> Die vorhandene CHF- und QR-Rechnungsfunktion bleibt unverändert aktiv. Eine spätere Bankanbindung wird ausschließlich ergänzend und nach Schweizer Providerprüfung eingerichtet.</>
                    ) : (
                      <>Cockpit-Summen unterschiedlicher Währungen werden ohne dokumentierten Wechselkurs getrennt ausgewiesen. Bestehende Rechnungs- und Angebotswerte werden nicht umgerechnet.</>
                    )}
                  </div>
                </div>
                <Button onClick={saveCompany} className="mt-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="btn-save-company">
                  <Save className="w-4 h-4 mr-1" /> Firmendaten speichern
                </Button>
              </Card>

              {/* Pakete-Bibliothek – nur für Angebots-/Rechnungsstudio */}
              {hasFeature("quotes") && (
              <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-packages-card">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)]">
                    Pakete-Bibliothek
                  </h2>
                </div>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Erstellen Sie wiederverwendbare Pakete, die Sie im Editor mit einem Klick zu Angeboten hinzufügen.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    {packages.length === 0 && (
                      <p className="text-sm text-[var(--brand-muted)] italic">Noch keine Pakete vorhanden.</p>
                    )}
                    {packages.map((p) => (
                      <div key={p.id} className="rounded border border-[var(--brand-border)] p-3 flex items-start justify-between gap-2" data-testid={`pkg-row-${p.id}`}>
                        <div>
                          <div className="font-medium text-[var(--brand-ink)]">{p.titel}</div>
                          <div className="text-xs text-[var(--brand-primary)]">{fmtCurrency(p.preis)} {p.einheit}</div>
                          <div className="text-xs text-[var(--brand-muted)] mt-1">{(p.bullets || []).length} Leistungspunkte</div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deletePackage(p.id)} data-testid={`btn-delete-pkg-${p.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3 rounded border border-[var(--brand-border)] p-4 bg-[var(--brand-bg)]">
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">Neues Paket</h3>
                    <div>
                      <Label>Titel</Label>
                      <Input value={newPkg.titel} onChange={(e) => setNewPkg({ ...newPkg, titel: e.target.value })} placeholder="z. B. Premium-Paket" data-testid="newpkg-titel" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Preis (€)</Label>
                        <Input type="number" value={newPkg.preis} onChange={(e) => setNewPkg({ ...newPkg, preis: e.target.value })} data-testid="newpkg-preis" />
                      </div>
                      <div>
                        <Label>Einheit</Label>
                        <Input value={newPkg.einheit} onChange={(e) => setNewPkg({ ...newPkg, einheit: e.target.value })} placeholder="/ Monat" data-testid="newpkg-einheit" />
                      </div>
                    </div>
                    <div>
                      <Label>Leistungen (eine pro Zeile)</Label>
                      <Textarea rows={4} value={newPkg.bullets} onChange={(e) => setNewPkg({ ...newPkg, bullets: e.target.value })} placeholder={"Leistung 1\nLeistung 2"} data-testid="newpkg-bullets" />
                    </div>
                    <Button onClick={createPackage} className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="btn-create-package">
                      <Plus className="w-4 h-4 mr-1" /> Paket erstellen
                    </Button>
                  </div>
                </div>
              </Card>
              )}

              {/* Zahlungsanbieter – nur für Angebots-/Rechnungsstudio */}
              {hasFeature("quotes") && (
              <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-payment-card">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">
                  Zahlungsanbieter
                </h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Hinterlegen Sie Ihre EIGENEN Stripe-/PayPal-Schlüssel. Damit kann Ihr Kunde Rechnungen direkt online bezahlen – der Status wird automatisch auf „Bezahlt“ gesetzt.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">Stripe</h3>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={payForm.stripe_enabled} onChange={(e) => setPayForm({ ...payForm, stripe_enabled: e.target.checked })} className="w-4 h-4 accent-[var(--brand-primary)]" data-testid="pay-stripe-enabled" />
                        aktiv
                      </label>
                    </div>
                    <div>
                      <Label>Stripe Secret Key {payCfg.has_stripe_key && <span className="text-[10px] text-green-600">(hinterlegt)</span>}</Label>
                      <Input type="password" value={payForm.stripe_secret_key} onChange={(e) => setPayForm({ ...payForm, stripe_secret_key: e.target.value })} placeholder={payCfg.has_stripe_key ? "•••••• (leer lassen = unverändert)" : "sk_live_… oder sk_test_…"} data-testid="pay-stripe-key" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">PayPal</h3>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={payForm.paypal_enabled} onChange={(e) => setPayForm({ ...payForm, paypal_enabled: e.target.checked })} className="w-4 h-4 accent-[var(--brand-primary)]" data-testid="pay-paypal-enabled" />
                        aktiv
                      </label>
                    </div>
                    <div>
                      <Label>PayPal Client ID {payCfg.has_paypal_key && <span className="text-[10px] text-green-600">(hinterlegt)</span>}</Label>
                      <Input value={payForm.paypal_client_id} onChange={(e) => setPayForm({ ...payForm, paypal_client_id: e.target.value })} data-testid="pay-paypal-client" />
                    </div>
                    <div>
                      <Label>PayPal Secret</Label>
                      <Input type="password" value={payForm.paypal_secret} onChange={(e) => setPayForm({ ...payForm, paypal_secret: e.target.value })} placeholder={payCfg.has_paypal_key ? "•••••• (leer lassen = unverändert)" : ""} data-testid="pay-paypal-secret" />
                    </div>
                    <div>
                      <Label>Modus</Label>
                      <select value={payForm.paypal_mode} onChange={(e) => setPayForm({ ...payForm, paypal_mode: e.target.value })} className="w-full border border-[var(--brand-border)] rounded px-2 py-2 text-sm bg-white" data-testid="pay-paypal-mode">
                        <option value="sandbox">Sandbox (Test)</option>
                        <option value="live">Live</option>
                      </select>
                    </div>
                  </div>
                </div>
                <Button onClick={savePayment} className="mt-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="btn-save-payment">
                  <Save className="w-4 h-4 mr-1" /> Zahlungseinstellungen speichern
                </Button>
              </Card>
              )}

              {/* E-Mail-Versand */}
              <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-email-card">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">
                  E-Mail-Versand (Resend)
                </h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Hinterlegen Sie Ihren EIGENEN Resend-API-Key und Absender. Kunden erhalten den Angebots-Link dann automatisch beim Status „Versendet“ – oder per Button im Editor. (Resend → API Keys, beginnt mit <code>re_…</code>; Absender-Domain muss bei Resend verifiziert sein.)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Resend API Key {emailCfg.has_api_key && <span className="text-[10px] text-green-600">(hinterlegt)</span>}</Label>
                    <Input type="password" value={emailForm.resend_api_key} onChange={(e) => setEmailForm({ ...emailForm, resend_api_key: e.target.value })} placeholder={emailCfg.has_api_key ? "•••••• (leer lassen = unverändert)" : "re_…"} data-testid="email-api-key" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                      <input type="checkbox" checked={emailForm.enabled} onChange={(e) => setEmailForm({ ...emailForm, enabled: e.target.checked })} className="w-4 h-4 accent-[var(--brand-primary)]" data-testid="email-enabled" />
                      E-Mail-Versand aktiv
                    </label>
                  </div>
                  <div>
                    <Label>Absender-E-Mail</Label>
                    <Input value={emailForm.sender_email} onChange={(e) => setEmailForm({ ...emailForm, sender_email: e.target.value })} placeholder="angebot@ihre-domain.de" data-testid="email-sender" />
                  </div>
                  <div>
                    <Label>Absender-Name</Label>
                    <Input value={emailForm.sender_name} onChange={(e) => setEmailForm({ ...emailForm, sender_name: e.target.value })} placeholder={companyForm.company_name || "Ihre Firma"} data-testid="email-sender-name" />
                  </div>
                </div>
                <Button onClick={saveEmail} className="mt-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="btn-save-email">
                  <Save className="w-4 h-4 mr-1" /> E-Mail-Einstellungen speichern
                </Button>
              </Card>

              {/* Team & Mitarbeiter (Multi-User) */}
              <TeamCard hasFeature={hasFeature} />

              {/* Audit-Log & API-Zugriff (Enterprise) */}
              <AuditLogCard hasFeature={hasFeature} />
              <ApiKeysCard hasFeature={hasFeature} />

              {/* Angebots-Follow-up */}
              {hasFeature("crm") && (
              <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-followup-card">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">
                  Automatische Angebots-Follow-ups
                </h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Fasse automatisch nach, wenn ein Angebot nach X Tagen noch offen ist – der Kunde erhält <b>einmalig</b> eine freundliche Nachfass-E-Mail mit Link zum Angebot. Läuft täglich automatisch. Voraussetzung: E-Mail-Versand oben ist aktiv.
                </p>
                <div className="rounded-lg border border-[var(--brand-border-light)] p-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
                    <input type="checkbox" checked={!!remindersForm.followup_enabled} onChange={(e) => setRemindersForm({ ...remindersForm, followup_enabled: e.target.checked })} className="w-4 h-4 accent-[var(--brand-primary)]" data-testid="followup-enabled" />
                    <span className="font-medium text-[var(--brand-ink)]">Follow-up für offene Angebote aktivieren</span>
                  </label>
                  <div className="flex items-end gap-3">
                    <div className="w-40">
                      <Label>Tage nach Versand</Label>
                      <Input type="number" min={1} max={60} value={remindersForm.followup_days} onChange={(e) => setRemindersForm({ ...remindersForm, followup_days: parseInt(e.target.value || "0", 10) })} data-testid="followup-days" />
                    </div>
                    <Button variant="outline" onClick={runFollowupNow} className="border-[var(--brand-border)]" data-testid="btn-run-followup">
                      Jetzt testen
                    </Button>
                  </div>
                </div>
                <Button onClick={saveReminders} disabled={remindersSaving} className="mt-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="btn-save-followup">
                  <Save className="w-4 h-4 mr-1" /> {remindersSaving ? "Speichert …" : "Follow-up speichern"}
                </Button>
              </Card>
              )}

              {/* Zahlungserinnerungen & Mahnlauf */}
              {hasFeature("mahnlauf") && (
              <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-reminders-card">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">
                  Zahlungserinnerungen &amp; Mahnlauf
                </h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Erinnern Sie Kunden automatisch per E-Mail – <b>vor</b> der Fälligkeit (freundliche Zahlungserinnerung) und <b>nach</b> Überschreitung (Mahnlauf). Läuft täglich automatisch. Voraussetzung: E-Mail-Versand oben ist aktiv.
                </p>

                <div className="rounded-lg border border-[var(--brand-border-light)] p-4 mb-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
                    <input type="checkbox" checked={remindersForm.predue_enabled} onChange={(e) => setRemindersForm({ ...remindersForm, predue_enabled: e.target.checked })} className="w-4 h-4 accent-[var(--brand-primary)]" data-testid="predue-enabled" />
                    <span className="font-medium text-[var(--brand-ink)]">Vorab-Erinnerung vor Fälligkeit aktivieren</span>
                  </label>
                  <div className="flex items-end gap-3">
                    <div className="w-40">
                      <Label>Tage vor Fälligkeit</Label>
                      <Input type="number" min={1} max={30} value={remindersForm.predue_days} onChange={(e) => setRemindersForm({ ...remindersForm, predue_days: parseInt(e.target.value || "0", 10) })} data-testid="predue-days" />
                    </div>
                    <Button variant="outline" onClick={runPredueNow} className="border-[var(--brand-border)]" data-testid="btn-run-predue">
                      Jetzt testen
                    </Button>
                  </div>
                  <p className="text-xs text-[var(--brand-muted)] mt-2">Kunden erhalten <b>einmalig</b> eine freundliche Erinnerung, sobald die Rechnung in {remindersForm.predue_days} Tag(en) fällig wird.</p>
                </div>

                <div className="rounded-lg border border-[var(--brand-border-light)] p-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
                    <input type="checkbox" checked={remindersForm.enabled} onChange={(e) => setRemindersForm({ ...remindersForm, enabled: e.target.checked })} className="w-4 h-4 accent-[var(--brand-primary)]" data-testid="dunning-enabled" />
                    <span className="font-medium text-[var(--brand-ink)]">Automatischer Mahnlauf nach Fälligkeit aktivieren</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[0, 1, 2].map((i) => (
                      <div key={i}>
                        <Label>Stufe {i + 1}: Tage überfällig</Label>
                        <Input type="number" min={1} value={remindersForm.days[i]} onChange={(e) => {
                          const d = [...remindersForm.days]; d[i] = parseInt(e.target.value || "0", 10); setRemindersForm({ ...remindersForm, days: d });
                        }} data-testid={`dunning-days-${i}`} />
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={saveReminders} disabled={remindersSaving} className="mt-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="btn-save-reminders">
                  <Save className="w-4 h-4 mr-1" /> {remindersSaving ? "Speichert …" : "Erinnerungen speichern"}
                </Button>
              </Card>
              )}

              {hasFeature("dashboard") && <ReportSettingsCard />}

              <Card className="p-6 bg-white border-[var(--brand-border)]">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">
                  Steuerdaten
                </h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Diese Angaben erscheinen automatisch auf jedem Angebot/jeder
                  Rechnung – einmal hinterlegen, immer korrekt.
                </p>
                <div className="space-y-3">
                  {(companyForm?.country || "DE") === "DE" && (
                    <div>
                      <Label className="mb-1.5 block">Steuerstatus</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tax-status-group">
                        <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition ${steuer.tax_status !== "kleinunternehmer" ? "border-[var(--brand-primary)] bg-[var(--brand-bg)]" : "border-[var(--brand-border)]"}`}>
                          <input type="radio" name="tax_status" checked={steuer.tax_status !== "kleinunternehmer"}
                            onChange={() => setSteuer({ ...steuer, tax_status: "regelbesteuert" })}
                            className="mt-0.5 accent-[var(--brand-primary)]" data-testid="tax-status-regel" />
                          <span className="text-sm">
                            <span className="font-medium text-[var(--brand-ink)] block">Umsatzsteuerpflichtig</span>
                            <span className="text-xs text-[var(--brand-muted)]">Regelbesteuerung – MwSt wird auf Angeboten &amp; Rechnungen ausgewiesen (19 %, 7 %, länderspezifisch)</span>
                          </span>
                        </label>
                        <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition ${steuer.tax_status === "kleinunternehmer" ? "border-[var(--brand-primary)] bg-[var(--brand-bg)]" : "border-[var(--brand-border)]"}`}>
                          <input type="radio" name="tax_status" checked={steuer.tax_status === "kleinunternehmer"}
                            onChange={() => setSteuer({ ...steuer, tax_status: "kleinunternehmer" })}
                            className="mt-0.5 accent-[var(--brand-primary)]" data-testid="tax-status-kleinunternehmer" />
                          <span className="text-sm">
                            <span className="font-medium text-[var(--brand-ink)] block">Kleinunternehmer (§ 19 UStG)</span>
                            <span className="text-xs text-[var(--brand-muted)]">Keine USt auf Dokumenten, Preise = Endpreise, Hinweis „Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." erscheint automatisch</span>
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>Steuernummer</Label>
                    <Input
                      value={steuer.steuernummer}
                      onChange={(e) =>
                        setSteuer({ ...steuer, steuernummer: e.target.value })
                      }
                      placeholder="z. B. 25/123/45678"
                      data-testid="input-steuernummer"
                    />
                  </div>
                  <div>
                    <Label>USt-IdNr. (optional)</Label>
                    <Input
                      value={steuer.ustid}
                      onChange={(e) =>
                        setSteuer({ ...steuer, ustid: e.target.value })
                      }
                      placeholder="z. B. DE123456789"
                      data-testid="input-ustid"
                    />
                  </div>
                  <div>
                    <Label>Zuständiges Finanzamt (optional)</Label>
                    <Input
                      value={steuer.finanzamt}
                      onChange={(e) =>
                        setSteuer({ ...steuer, finanzamt: e.target.value })
                      }
                      data-testid="input-finanzamt"
                    />
                  </div>

                  <Separator />
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-[var(--brand-primary)]">
                    Bankverbindung (für Rechnungen)
                  </h3>
                  <div>
                    <Label>Kontoinhaber</Label>
                    <Input
                      value={steuer.kontoinhaber}
                      onChange={(e) =>
                        setSteuer({ ...steuer, kontoinhaber: e.target.value })
                      }
                      placeholder={company?.company_name || "Kontoinhaber"}
                      data-testid="input-kontoinhaber"
                    />
                  </div>
                  <div>
                    <Label>IBAN</Label>
                    <Input
                      value={steuer.iban}
                      onChange={(e) => setSteuer({ ...steuer, iban: e.target.value })}
                      placeholder="DE00 0000 0000 0000 0000 00"
                      data-testid="input-iban"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>BIC</Label>
                      <Input
                        value={steuer.bic}
                        onChange={(e) => setSteuer({ ...steuer, bic: e.target.value })}
                        data-testid="input-bic"
                      />
                    </div>
                    <div>
                      <Label>Bank</Label>
                      <Input
                        value={steuer.bank}
                        onChange={(e) => setSteuer({ ...steuer, bank: e.target.value })}
                        placeholder="z. B. Sparkasse Hannover"
                        data-testid="input-bank"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={saveSteuer}
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white w-full"
                    data-testid="btn-save-steuer"
                  >
                    <Save className="w-4 h-4 mr-1" /> Steuerdaten speichern
                  </Button>
                  <div
                    className="text-[11px] text-[var(--brand-muted)] rounded p-2"
                    style={{ background: "var(--brand-bg)" }}
                  >
                    Hinweis: Diese App ersetzt keinen Steuerberater. Für
                    konkrete Pflichtangaben bei Auslandskunden bitte mit
                    deinem Steuerberater abklären.
                  </div>
                </div>
              </Card>
              {hasFeature("datev") && (

              <Card className="p-6 bg-white border-[var(--brand-border)]" data-testid="datev-settings-card">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">
                  DATEV-Export (Steuerberater)
                </h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Für den EXTF-Buchungsstapel-Export. Standardkonten = SKR03. Im Zweifel mit dem Steuerberater abstimmen.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Beraternummer</Label>
                    <Input value={datev.berater_nr} onChange={(e) => setDatev({ ...datev, berater_nr: e.target.value })} data-testid="datev-berater" />
                  </div>
                  <div>
                    <Label>Mandantennummer</Label>
                    <Input value={datev.mandant_nr} onChange={(e) => setDatev({ ...datev, mandant_nr: e.target.value })} data-testid="datev-mandant" />
                  </div>
                  <div>
                    <Label>Debitor-Sammelkonto</Label>
                    <Input value={datev.debitor_sammelkonto} onChange={(e) => setDatev({ ...datev, debitor_sammelkonto: e.target.value })} data-testid="datev-debitor" />
                  </div>
                  <div>
                    <Label>Sachkontenlänge</Label>
                    <Input type="number" value={datev.konto_laenge} onChange={(e) => setDatev({ ...datev, konto_laenge: Number(e.target.value) })} data-testid="datev-kontolaenge" />
                  </div>
                  <div>
                    <Label>Erlöskonto 19/20%</Label>
                    <Input value={datev.erloes_19} onChange={(e) => setDatev({ ...datev, erloes_19: e.target.value })} data-testid="datev-erloes19" />
                  </div>
                  <div>
                    <Label>Erlöskonto 7/ermäßigt</Label>
                    <Input value={datev.erloes_7} onChange={(e) => setDatev({ ...datev, erloes_7: e.target.value })} data-testid="datev-erloes7" />
                  </div>
                  <div>
                    <Label>Erlöskonto steuerfrei/0%</Label>
                    <Input value={datev.erloes_0} onChange={(e) => setDatev({ ...datev, erloes_0: e.target.value })} data-testid="datev-erloes0" />
                  </div>
                </div>
                <Button onClick={saveDatev} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white w-full mt-4" data-testid="btn-save-datev">
                  <Save className="w-4 h-4 mr-1" /> DATEV-Einstellungen speichern
                </Button>
              </Card>
              )}

              <Card className="p-6 bg-white border-[var(--brand-border)]">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">
                  Deine Unterschrift
                </h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Wird automatisch unter &bdquo;Mit besten Gr&uuml;&szlig;en&ldquo; auf jedem Angebot
                  und jeder Rechnung angezeigt.
                </p>

                {signature.exists ? (
                  <div className="flex items-center gap-3 p-3 rounded border border-[var(--brand-border)] bg-[var(--brand-bg)]">
                    <img
                      src={signature.data_url}
                      alt="Unterschrift"
                      className="max-h-16 max-w-[180px] object-contain"
                      data-testid="sig-preview"
                    />
                    <div className="flex-1" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSigDraft("");
                        setSigPadOpen(true);
                      }}
                      data-testid="btn-redraw-sig"
                    >
                      <PenTool className="w-4 h-4 mr-1" /> Neu zeichnen
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-700 border-red-200 hover:bg-red-50"
                      onClick={removeSignature}
                      data-testid="btn-remove-sig"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => {
                        setSigDraft("");
                        setSigPadOpen(true);
                      }}
                      className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                      data-testid="btn-draw-sig"
                    >
                      <PenTool className="w-4 h-4 mr-1" /> Unterschrift zeichnen
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setAutoSigOpen(true)}
                      data-testid="btn-auto-sig"
                    >
                      <PenTool className="w-4 h-4 mr-1" /> Schrift-Unterschrift
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => fileInputRefs.current["sig"]?.click()}
                      data-testid="btn-upload-sig"
                    >
                      <Upload className="w-4 h-4 mr-1" /> Bild hochladen
                    </Button>
                    <input
                      ref={(el) => (fileInputRefs.current["sig"] = el)}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        uploadSignatureFile(f);
                      }}
                    />
                  </div>
                )}
              </Card>

              <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-attachments-card">
                <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1">
                  Eigene Dokumente / Anlagen
                </h2>
                <p className="text-sm text-[var(--brand-muted)] mb-4">
                  Lade hier deine eigenen PDF-Dokumente hoch (z. B. AGB, Verträge, Datenschutzhinweise – ganz wie du möchtest) und benenne sie frei. Pro Angebot/Rechnung wählst du im Editor per Häkchen, welche davon ans finale PDF angehängt werden.
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--brand-primary)] text-white text-sm cursor-pointer hover:bg-[var(--brand-primary-dark)]">
                    <Upload className="w-4 h-4" /> PDF hochladen
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      data-testid="attachment-upload-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        uploadAttachment(f);
                      }}
                    />
                  </label>
                  <span className="text-xs text-[var(--brand-muted)]">PDF, max. 8 MB</span>
                </div>

                <div className="space-y-3">
                  {attachments.length === 0 && (
                    <p className="text-sm text-[var(--brand-muted)] italic">Noch keine Dokumente hochgeladen.</p>
                  )}
                  {attachments.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-3 p-3 rounded border border-[var(--brand-border)] bg-[var(--brand-bg)]"
                      data-testid={`attachment-row-${a.id}`}
                    >
                      <FileText className="w-6 h-6 text-[var(--brand-primary)]" />
                      <div className="flex-1 min-w-0">
                        <input
                          defaultValue={a.label}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== a.label) renameAttachment(a.id, v);
                          }}
                          className="font-semibold text-[var(--brand-ink)] bg-transparent border-b border-transparent focus:border-[var(--brand-border)] focus:outline-none w-full"
                          data-testid={`attachment-label-${a.id}`}
                        />
                        <div className="text-xs text-[var(--brand-muted)] truncate">
                          {a.filename}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-700 border-red-200 hover:bg-red-50"
                        onClick={() => removeAttachment(a.id)}
                        data-testid={`btn-remove-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

);
